import React from 'react';
import {
  FlightTakeoff,
  Battery80,
  CameraAlt,
  People,
  Business,
  Approval,
  Description,
  Folder,
  Settings,
  PersonAdd,
  PersonRemove,
  SwapHoriz,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Warning,
  Info
} from '@mui/icons-material';
import { Avatar } from '@mui/material';

/**
 * Get icon component based on activity type and category
 */
const getIconByActivityType = (activityType, category) => {
  // FLEET category
  if (category === 'FLEET') {
    if (activityType.includes('DRONE')) return FlightTakeoff;
    if (activityType.includes('BATTERY')) return Battery80;
    if (activityType.includes('PAYLOAD')) return CameraAlt;
  }

  // TEAM category
  if (category === 'TEAM') {
    if (activityType.includes('INVITED')) return PersonAdd;
    if (activityType.includes('JOINED')) return PersonAdd;
    if (activityType.includes('REMOVED')) return PersonRemove;
    if (activityType.includes('ROLE_CHANGED')) return SwapHoriz;
    return People;
  }

  // LAANC category
  if (category === 'LAANC') {
    if (activityType.includes('APPROVED')) return CheckCircle;
    if (activityType.includes('DENIED')) return Cancel;
    if (activityType.includes('CANCELLED')) return Cancel;
    if (activityType.includes('EXPIRED')) return Warning;
    return Approval;
  }

  // ORGANIZATION category
  if (category === 'ORGANIZATION') {
    if (activityType.includes('SETTINGS')) return Settings;
    return Business;
  }

  // MISSION category
  if (category === 'MISSION') {
    return Description;
  }

  // FILES category
  if (category === 'FILES') {
    return Folder;
  }

  // COMPLIANCE category
  if (category === 'COMPLIANCE') {
    return Description;
  }

  // Default based on action
  if (activityType.includes('CREATED') || activityType.includes('ADD')) return Add;
  if (activityType.includes('UPDATED') || activityType.includes('EDIT')) return Edit;
  if (activityType.includes('DELETED') || activityType.includes('REMOVE')) return Delete;

  return Info;
};

/**
 * Get color based on category and severity
 */
const getColorByCategory = (category, severity) => {
  // Severity takes precedence
  if (severity === 'ERROR') return '#f44336'; // Red
  if (severity === 'WARNING') return '#ff9800'; // Orange

  // Category colors
  switch (category) {
    case 'FLEET':
      return '#2196f3'; // Blue
    case 'TEAM':
      return '#9c27b0'; // Purple
    case 'LAANC':
      return '#4caf50'; // Green
    case 'MISSION':
      return '#00bcd4'; // Cyan
    case 'ORGANIZATION':
      return '#ff5722'; // Deep Orange
    case 'FILES':
      return '#607d8b'; // Blue Grey
    case 'COMPLIANCE':
      return '#ffc107'; // Amber
    default:
      return '#757575'; // Grey
  }
};

/**
 * ActivityIcon component - displays icon with category/severity-based styling
 */
const ActivityIcon = ({ activityType, category, severity = 'INFO', size = 'medium' }) => {
  const IconComponent = getIconByActivityType(activityType, category);
  const color = getColorByCategory(category, severity);

  const sizeMap = {
    small: { avatar: 32, icon: 16 },
    medium: { avatar: 40, icon: 20 },
    large: { avatar: 48, icon: 24 }
  };

  const dimensions = sizeMap[size] || sizeMap.medium;

  return (
    <Avatar
      sx={{
        bgcolor: color,
        width: dimensions.avatar,
        height: dimensions.avatar
      }}
    >
      <IconComponent sx={{ fontSize: dimensions.icon, color: '#fff' }} />
    </Avatar>
  );
};

export default ActivityIcon;
